/**
 * DnsProviderSettings — credenciales de proveedores DNS (SOLO superadmin).
 *
 * Es configuración de PLATAFORMA: los clientes no ven esta pantalla ni pueden
 * leer los secretos. El backend devuelve las claves enmascaradas y con banderas
 * `has_*`; un PUT con el campo vacío significa «no lo cambies».
 *
 * Para OVH hacen falta las tres claves del token de API:
 *   Application Key · Application Secret · Consumer Key
 * El endpoint por defecto es `ovh-eu` (Europa).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ServerStackIcon,
  PlusIcon,
  PlayIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import { usuarioActual } from './feedbackConfig';

const FORM_VACIO = {
  id: null,
  name: '',
  provider: 'ovh',
  endpoint: 'ovh-eu',
  application_key: '',
  application_secret: '',
  consumer_key: '',
  is_active: true,
  notes: '',
};

const ENDPOINTS = ['ovh-eu', 'ovh-ca', 'ovh-us', 'kimsufi-eu', 'soyoustart-eu'];

export default function DnsProviderSettings() {
  const superadmin = useMemo(() => usuarioActual().superadmin, []);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [form, setForm] = useState(FORM_VACIO);
  const [editando, setEditando] = useState(false);
  const [saving, setSaving] = useState(false);
  const [probando, setProbando] = useState(null);

  const cargar = useCallback(async () => {
    if (!superadmin) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.getDnsProviders();
      setProveedores(res.data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los proveedores');
    } finally {
      setLoading(false);
    }
  }, [superadmin]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      delete payload.id;
      if (form.id) await api.updateDnsProvider(form.id, payload);
      else await api.createDnsProvider(payload);
      setEditando(false);
      setForm(FORM_VACIO);
      await cargar();
      setAviso('Proveedor guardado');
      setTimeout(() => setAviso(''), 2500);
    } catch (err) {
      setError(err.message || 'No se pudo guardar el proveedor');
    } finally {
      setSaving(false);
    }
  };

  const probar = async (proveedor) => {
    setProbando(proveedor.id);
    setError('');
    try {
      const res = await api.testDnsProvider(proveedor.id);
      setAviso(res.message || 'Prueba completada');
      await cargar();
    } catch (err) {
      setError(err.message || 'La prueba falló');
    } finally {
      setProbando(null);
    }
  };

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Proveedor',
      sortable: true,
      render: (p) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <ShieldCheckIcon style={{ width: 15, height: 15, color: 'var(--accent-l)' }} />
          <strong>{p.name}</strong>
        </span>
      ),
    },
    { key: 'provider_label', label: 'Tipo', sortable: true, render: (p) => <span className="list-badge">{p.provider_label}</span> },
    { key: 'endpoint', label: 'Endpoint', sortable: true, render: (p) => <span className="list-code">{p.endpoint}</span> },
    {
      key: 'application_key',
      label: 'Application key',
      render: (p) => (p.has_application_key ? <span className="list-code">{p.application_key}</span> : <span className="list-hint">—</span>),
    },
    {
      key: 'secrets',
      label: 'Secretos',
      render: (p) => (
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <span className={`list-badge ${p.has_application_secret ? 'list-badge--on' : 'list-badge--off'}`}>secret</span>
          <span className={`list-badge ${p.has_consumer_key ? 'list-badge--on' : 'list-badge--off'}`}>consumer</span>
        </span>
      ),
    },
    {
      key: 'last_check_ok',
      label: 'Última prueba',
      render: (p) => {
        if (!p.last_check_at) return <span className="list-hint">—</span>;
        return (
          <span className={`list-badge ${p.last_check_ok ? 'list-badge--on' : 'list-badge--off'}`} title={p.last_check_message || ''}>
            {p.last_check_ok ? 'OK' : 'Fallo'} · {new Date(p.last_check_at).toLocaleDateString()}
          </span>
        );
      },
    },
  ], []);

  // El hook tiene que ejecutarse SIEMPRE en el mismo orden: el corte por rol va
  // después de los `useMemo`, no antes.
  if (!superadmin) {
    return (
      <div className="list-page">
        <div className="empty-state">
          <ExclamationTriangleIcon style={{ width: 34, height: 34, opacity: 0.7 }} />
          <h3>Solo el superadmin</h3>
          <p className="empty-subtitle">
            Los proveedores DNS son configuración global de la plataforma: guardan credenciales
            de API y no se muestran a los clientes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ListPage
        icon={ServerStackIcon}
        title="Proveedores DNS"
        subtitle="Credenciales de API para leer y escribir zonas (OVH). Solo superadmin; los clientes no ven esta pantalla."
        search=""
        columns={columns}
        rows={proveedores}
        loading={loading}
        error={error}
        onCreate={() => { setForm(FORM_VACIO); setEditando(true); }}
        createLabel="Nuevo proveedor"
        empty={{
          icon: '🔐',
          title: 'Sin proveedores',
          subtitle: 'Da de alta un token de API de OVH para poder sincronizar y editar zonas.',
          action: (
            <button type="button" className="list-btn-primary" onClick={() => { setForm(FORM_VACIO); setEditando(true); }}>
              <PlusIcon style={{ width: 14, height: 14 }} /> Nuevo proveedor
            </button>
          ),
        }}
        actions={{
          extra: (row) => (
            <button
              type="button"
              className="list-action-btn"
              title="Probar la conexión"
              onClick={() => probar(row)}
              disabled={probando === row.id}
            >
              <PlayIcon style={{ width: 14, height: 14 }} />
            </button>
          ),
          onEdit: (row) => { setForm({ ...FORM_VACIO, ...row, application_key: '', application_secret: '', consumer_key: '' }); setEditando(true); },
          onDelete: async (row) => { await api.deleteDnsProvider(row.id); await cargar(); },
          deleteTitle: 'Eliminar proveedor',
          deleteMessage: (row) => `¿Eliminar el proveedor «${row.name}»? Los dominios que lo usaban se quedan solo en la CMDB.`,
        }}
      />

      {aviso && <div className="list-badge list-badge--accent" style={{ position: 'fixed', bottom: 18, right: 18, zIndex: 1100 }}>{aviso}</div>}

      {editando && (
        <Dialog
          title={form.id ? `Editar ${form.name}` : 'Nuevo proveedor DNS'}
          icon={<ServerStackIcon style={{ width: 18, height: 18 }} />}
          maxWidth={640}
          busy={saving}
          onClose={() => setEditando(false)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setEditando(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={guardar} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <label className="list-hint">
              Nombre
              <input className="list-input" style={{ width: '100%', marginTop: 3 }} placeholder="OVH principal"
                value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </label>
            <label className="list-hint">
              Endpoint
              <select className="list-select" style={{ width: '100%', marginTop: 3 }}
                value={form.endpoint} onChange={(e) => setForm((p) => ({ ...p, endpoint: e.target.value }))}>
                {ENDPOINTS.map((endpoint) => <option key={endpoint} value={endpoint}>{endpoint}</option>)}
              </select>
            </label>
            <label className="list-hint">
              Application key
              <input className="list-input" style={{ width: '100%', marginTop: 3 }}
                placeholder={form.id ? '(sin cambios)' : ''}
                value={form.application_key} onChange={(e) => setForm((p) => ({ ...p, application_key: e.target.value }))} />
            </label>
            <label className="list-hint">
              Application secret
              <input className="list-input" type="password" style={{ width: '100%', marginTop: 3 }}
                placeholder={form.id ? '(sin cambios)' : ''}
                value={form.application_secret} onChange={(e) => setForm((p) => ({ ...p, application_secret: e.target.value }))} />
            </label>
            <label className="list-hint">
              Consumer key
              <input className="list-input" type="password" style={{ width: '100%', marginTop: 3 }}
                placeholder={form.id ? '(sin cambios)' : ''}
                value={form.consumer_key} onChange={(e) => setForm((p) => ({ ...p, consumer_key: e.target.value }))} />
            </label>
            <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 20 }}>
              <input type="checkbox" checked={Boolean(form.is_active)}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
              Activo
            </label>
            <label className="list-hint" style={{ gridColumn: '1 / -1' }}>
              Notas
              <textarea className="list-input" rows={2} style={{ width: '100%', marginTop: 3 }}
                value={form.notes || ''} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
            </label>
          </div>
          <p className="list-hint" style={{ marginTop: 10 }}>
            Las claves se guardan en la CMDB y nunca se devuelven al navegador en claro. Deja un
            campo vacío para conservar el valor actual.
          </p>
        </Dialog>
      )}
    </>
  );
}
